import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import ModalComponent from '../../../helper_components/ModalComponent';
import { hasPdfSignature, isReadablePdf } from './annexPdfValidation';
import {
  cachePreview,
  evictCachedPreview,
  getCachedPreview,
  peekCachedPreview,
} from './annexPreviewCache';

const SUPPORTED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const LARGE_FILE_SIZE_MB = 50;
const LARGE_FILE_BYTES = LARGE_FILE_SIZE_MB * 1024 * 1024;
const LOAD_WARN_TIMEOUT_MS = 3000;

const STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  ACCESS_DENIED: 'access_denied',
  EXPIRED: 'expired',
  NOT_FOUND: 'not_found',
  CORRUPT: 'corrupt',
  NETWORK_ERROR: 'network_error',
  SERVER_ERROR: 'server_error',
};

const MESSAGES = {
  [STATUS.ACCESS_DENIED]: 'Acceso no autorizado',
  [STATUS.EXPIRED]:
    'El enlace de previsualización expiró. Recargue la página para generar uno nuevo.',
  [STATUS.NOT_FOUND]:
    'El archivo del anexo no está disponible en este momento. Intente descargarlo más tarde.',
  [STATUS.CORRUPT]:
    'El archivo no se puede previsualizar porque está corrupto. Intente descargarlo para abrirlo localmente.',
  [STATUS.NETWORK_ERROR]:
    'Ocurrió un error de red al cargar la previsualización. Puede reintentar o descargar el archivo.',
  [STATUS.SERVER_ERROR]:
    'El servidor no pudo entregar el archivo en este momento. Puede reintentar o descargar el archivo.',
};

/**
 * Unix timestamp in the `expires` query parameter of a signed URL.
 *
 * @param {string} url
 * @returns {number} 0 when the URL carries no valid expiry.
 */
const getSignatureExpiry = (url) => {
  try {
    const expires = parseInt(new URL(url).searchParams.get('expires') || '0', 10);
    return Number.isFinite(expires) ? expires : 0;
  } catch {
    return 0;
  }
};

/**
 * Whether the signature of the URL already expired.
 *
 * @param {string} url
 * @returns {boolean}
 */
const isSignatureExpired = (url) => {
  const expires = getSignatureExpiry(url);
  return expires > 0 && expires * 1000 < Date.now();
};

/**
 * Lowercase extension of a file name or URL, ignoring its query string.
 *
 * @param {string} [name]
 * @returns {string} Empty when the name has no extension.
 */
const getExtension = (name = '') => {
  const base = name.split('?')[0];
  const parts = base.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Preview type of an annex from its MIME type, declared extension or file name.
 *
 * @param {?{file_type?: string, extension?: string, filename?: string}} doc
 * @returns {'pdf'|'image'|'office'|'unsupported'}
 */
const getFileType = (doc) => {
  const mime = doc && doc.file_type ? doc.file_type.toLowerCase() : '';
  if (mime === 'application/pdf') return 'pdf';
  if (SUPPORTED_IMAGE_MIME.includes(mime)) return 'image';
  /* Fall back to the declared extension or the one in the file name. */
  const ext = (doc && doc.extension ? String(doc.extension) : getExtension(doc ? doc.filename : ''))
    .replace(/^\./, '')
    .toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (OFFICE_EXTENSIONS.includes(ext)) return 'office';
  return 'unsupported';
};

/**
 * Maps the error code of a core JSON response to a preview status.
 *
 * Core answers controlled errors (HttpResponse) with HTTP 200 and the real
 * code in the JSON body; an invalid or expired signature arrives as HTTP 401.
 * Any other code (throttling, server failures) is a transient server error
 * the user can retry, never a corrupt file.
 *
 * @param {?number} code
 * @returns {string} One of the STATUS values.
 */
const statusFromErrorCode = (code) => {
  if (code === 401 || code === 403) return STATUS.ACCESS_DENIED;
  if (code === 404) return STATUS.NOT_FOUND;
  return STATUS.SERVER_ERROR;
};

/**
 * Error code carried in the JSON body of a core response.
 *
 * @param {Response} resp
 * @returns {Promise<?number>} null when the body is not JSON or has no numeric code.
 */
const readJsonCode = async (resp) => {
  try {
    const body = await resp.json();
    return body && typeof body.code === 'number' ? body.code : null;
  } catch {
    return null;
  }
};

/**
 * Downloads the annex through its signed URL and checks it can be previewed.
 *
 * @param {string} url Signed URL of the annex.
 * @param {'pdf'|'image'} fileType
 * @param {AbortSignal} signal Cancels the request when the preview is closed.
 * @param {(size: number) => void} onSizeKnown Receives the Content-Length before the body is read.
 * @returns {Promise<{status: string, entry?: {objectUrl: string, size: number}}>}
 *   The preview status and, when it is ready, the object URL to render.
 */
const fetchPreview = async (url, fileType, signal, onSizeKnown) => {
  const resp = await fetch(url, { signal });
  const contentType = (resp.headers.get('Content-Type') || '').toLowerCase();

  if (resp.status === 401 || resp.status === 403) {
    return { status: isSignatureExpired(url) ? STATUS.EXPIRED : STATUS.ACCESS_DENIED };
  }

  if (contentType.includes('application/json')) {
    const code = await readJsonCode(resp);
    return { status: statusFromErrorCode(code === null ? resp.status : code) };
  }

  if (resp.status === 404) return { status: STATUS.NOT_FOUND };
  if (!resp.ok) return { status: STATUS.SERVER_ERROR };

  /*
   * The size is known from the headers before the body is read, so a large
   * file can be flagged while it is still downloading.
   */
  const contentLength = parseInt(resp.headers.get('Content-Length') || '0', 10);
  onSizeKnown(contentLength);
  const blob = await resp.blob();

  if (blob.size === 0) return { status: STATUS.CORRUPT };
  if (fileType === 'pdf' && !(await hasPdfSignature(blob))) return { status: STATUS.CORRUPT };

  /*
   * Forcing the expected MIME type lets the browser's native viewer render the
   * file inline even though core sends Content-Disposition: attachment.
   */
  const typedBlob =
    fileType === 'pdf' && blob.type !== 'application/pdf'
      ? new Blob([blob], { type: 'application/pdf' })
      : blob;
  const objectUrl = URL.createObjectURL(typedBlob);

  if (fileType === 'pdf' && !(await isReadablePdf(objectUrl, signal))) {
    URL.revokeObjectURL(objectUrl);
    return { status: STATUS.CORRUPT };
  }

  return {
    status: STATUS.READY,
    entry: {
      objectUrl,
      size: Math.max(blob.size, contentLength),
    },
  };
};

const LARGE_FILE_WARNING = `El archivo es mayor a ${LARGE_FILE_SIZE_MB} MB: la previsualización puede tardar o no responder. Si demora demasiado, descargue el archivo.`;

const SLOW_LOAD_WARNING =
  'La previsualización está tardando más de lo esperado. Si el archivo es muy grande, considere descargarlo.';

/**
 * Modal that previews a PDF or image annex and offers its download.
 *
 * @param {object} props
 * @param {?object} props.document Annex to preview; `file_path` is its signed URL.
 * @param {boolean} props.show
 * @param {() => void} props.onHide
 * @returns {JSX.Element}
 */
const AnnexPreviewModal = ({ document: doc, show, onHide }) => {
  const [result, setResult] = useState({ key: null, status: null });
  const [slowKey, setSlowKey] = useState(null);
  const [largeKey, setLargeKey] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [prevShow, setPrevShow] = useState(show);

  /*
   * Every opening of the modal is a new request, so an error from a previous
   * opening is not carried over to the next one.
   */
  if (show !== prevShow) {
    setPrevShow(show);
    if (show) setOpenCount((count) => count + 1);
  }

  /*
   * `file_path` is a signed URL (HMAC + expiry, see SignedDownloadUrl in core)
   * issued only for documents the authenticated user can see. It must not be
   * wrapped with the static build token.
   */
  const fileUrl = doc && doc.file_path ? doc.file_path : '';
  const hasFile = Boolean(fileUrl);
  const fileType = getFileType(doc);
  const isPreviewable = fileType === 'pdf' || fileType === 'image';
  const displayName = doc ? doc.alias || doc.filename || '' : '';
  const downloadName = doc ? doc.filename || doc.alias || '' : '';
  const requestKey =
    show && hasFile && isPreviewable ? `${fileUrl}#${openCount}#${retryKey}` : null;

  /*
   * A cached preview is only shown while the signature of the URL is valid,
   * the same window in which core would serve the file again.
   */
  const cached = requestKey && !isSignatureExpired(fileUrl) ? peekCachedPreview(fileUrl) : null;
  let status = STATUS.LOADING;
  if (cached) status = STATUS.READY;
  else if (requestKey && result.key === requestKey) status = result.status;
  else if (requestKey && isSignatureExpired(fileUrl)) status = STATUS.EXPIRED;

  const objectUrl = cached ? cached.objectUrl : '';
  /*
   * Downloads always go through the signed URL so core records them in the
   * audit log. There is nothing to download when the annex has no file, the
   * signature expired (core answers 401), core denied the access or the file
   * is missing in storage.
   */
  const canDownload =
    hasFile &&
    !isSignatureExpired(fileUrl) &&
    status !== STATUS.ACCESS_DENIED &&
    status !== STATUS.NOT_FOUND;
  let loadWarning = '';
  if (cached ? cached.size > LARGE_FILE_BYTES : largeKey === requestKey) {
    loadWarning = LARGE_FILE_WARNING;
  } else if (status === STATUS.LOADING && slowKey === requestKey) {
    loadWarning = SLOW_LOAD_WARNING;
  }

  useEffect(() => {
    if (!requestKey) return undefined;
    if (isSignatureExpired(fileUrl)) {
      /* A preview whose link expired can no longer be shown, so it is released. */
      evictCachedPreview(fileUrl);
      return undefined;
    }
    if (getCachedPreview(fileUrl)) return undefined;

    const controller = new AbortController();
    const slowTimer = setTimeout(() => setSlowKey(requestKey), LOAD_WARN_TIMEOUT_MS);

    /** @param {number} size Content-Length of the response in bytes. */
    const handleSizeKnown = (size) => {
      if (!controller.signal.aborted && size > LARGE_FILE_BYTES) setLargeKey(requestKey);
    };

    fetchPreview(fileUrl, fileType, controller.signal, handleSizeKnown)
      .then((preview) => {
        if (controller.signal.aborted) {
          if (preview.entry) URL.revokeObjectURL(preview.entry.objectUrl);
          return;
        }
        if (preview.entry) cachePreview(fileUrl, preview.entry);
        setResult({ key: requestKey, status: preview.status });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResult({ key: requestKey, status: STATUS.NETWORK_ERROR });
        }
      })
      .finally(() => clearTimeout(slowTimer));

    return () => {
      controller.abort();
      clearTimeout(slowTimer);
    };
  }, [requestKey, fileUrl, fileType]);

  /* A corrupt image is only detected when the browser decodes it. */
  const handleImageError = () => {
    evictCachedPreview(fileUrl);
    setResult({ key: requestKey, status: STATUS.CORRUPT });
  };

  const handleRetry = () => {
    evictCachedPreview(fileUrl);
    setRetryKey((k) => k + 1);
  };

  const handleDownload = () => {
    const a = window.document.createElement('a');
    a.href = fileUrl;
    a.download = downloadName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const renderDownloadSuggestion = () => (
    <Button variant="primary" size="sm" onClick={handleDownload} style={{ marginLeft: '8px' }}>
      <i className="ti-download mr-1" aria-hidden="true" /> Descargar archivo
    </Button>
  );

  /**
   * Status message with optional retry and download actions.
   *
   * @param {{icon: string, color: string, text: string, live?: string, retry?: boolean, download?: boolean}} options
   * @returns {JSX.Element}
   */
  const renderMessage = ({
    icon,
    color,
    text,
    live = 'polite',
    retry = false,
    download = canDownload,
  }) => (
    <div className="text-center p-4" role="alert" aria-live={live}>
      <i
        className={`fas ${icon} fa-3x ${color} mb-3`}
        style={{ display: 'block' }}
        aria-hidden="true"
      />
      <p style={{ fontSize: '1rem' }}>{text}</p>
      {retry && (
        <Button variant="warning" size="sm" onClick={handleRetry}>
          <i className="fas fa-redo" aria-hidden="true" /> Reintentar
        </Button>
      )}
      {download && renderDownloadSuggestion()}
    </div>
  );

  const renderContent = () => {
    if (!doc) return null;

    if (!hasFile) {
      return renderMessage({
        icon: 'fa-times-circle',
        color: 'text-danger',
        text: MESSAGES[STATUS.NOT_FOUND],
        live: 'assertive',
      });
    }

    if (fileType === 'office') {
      return renderMessage({
        icon: 'fa-file-word',
        color: 'text-warning',
        text: 'Los archivos de Office (Word, Excel, PowerPoint) no se pueden previsualizar directamente en el navegador y el sistema no cuenta con un visor de OneDrive o Google Docs integrado. Descargue el archivo para abrirlo con la aplicación correspondiente.',
      });
    }

    if (fileType === 'unsupported') {
      return renderMessage({
        icon: 'fa-exclamation-triangle',
        color: 'text-warning',
        text: 'Formato no compatible para previsualización. Por favor, descargue el archivo para ver su contenido.',
      });
    }

    if (status === STATUS.ACCESS_DENIED) {
      return renderMessage({
        icon: 'fa-lock',
        color: 'text-danger',
        text: MESSAGES[STATUS.ACCESS_DENIED],
        live: 'assertive',
        download: false,
      });
    }

    if (status === STATUS.EXPIRED) {
      return renderMessage({
        icon: 'fa-clock',
        color: 'text-warning',
        text: MESSAGES[STATUS.EXPIRED],
      });
    }

    if (status === STATUS.NOT_FOUND || status === STATUS.CORRUPT) {
      return renderMessage({
        icon: 'fa-times-circle',
        color: 'text-danger',
        text: MESSAGES[status],
        live: 'assertive',
      });
    }

    if (status === STATUS.NETWORK_ERROR || status === STATUS.SERVER_ERROR) {
      return renderMessage({
        icon: status === STATUS.NETWORK_ERROR ? 'fa-wifi' : 'fa-server',
        color: 'text-danger',
        text: MESSAGES[status],
        live: 'assertive',
        retry: true,
      });
    }

    return (
      <div>
        {loadWarning && (
          <div className="alert alert-warning" role="status" aria-live="polite">
            <i className="fas fa-hourglass-half mr-2" aria-hidden="true" />
            {loadWarning}
          </div>
        )}
        {status === STATUS.LOADING && (
          <div className="text-center p-4" role="status" aria-live="polite">
            <i className="fas fa-spinner fa-spin fa-2x mb-2" aria-hidden="true" />
            <p>Cargando previsualización...</p>
          </div>
        )}
        {status === STATUS.READY && fileType === 'pdf' && (
          <iframe
            src={objectUrl}
            title={`Previsualización de ${displayName}`}
            width="100%"
            height="550px"
            style={{ border: 'none', display: 'block' }}
            aria-label={`Visor de documento PDF: ${displayName}`}
          />
        )}
        {status === STATUS.READY && fileType === 'image' && (
          <div className="text-center">
            <img
              src={objectUrl}
              alt={`Previsualización de ${displayName}`}
              style={{ maxWidth: '100%', maxHeight: '550px', objectFit: 'contain' }}
              onError={handleImageError}
            />
          </div>
        )}
      </div>
    );
  };

  const renderFooter = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Button
        variant="primary"
        onClick={handleDownload}
        disabled={!canDownload}
        aria-label={`Descargar ${displayName}`}
      >
        <i className="ti-download mr-1" aria-hidden="true" /> Descargar
      </Button>
      <Button variant="secondary" onClick={onHide} aria-label="Cerrar previsualización">
        Cerrar
      </Button>
    </div>
  );

  return (
    <ModalComponent
      header={`Previsualización: ${displayName}`}
      body={renderContent()}
      footer={renderFooter()}
      show={show}
      canClose={true}
      onHide={onHide}
      size="lg"
      dialogClassName="annex-preview-modal"
    />
  );
};

export default AnnexPreviewModal;
